/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { RichText } from "@sitecore-jss/sitecore-jss-nextjs";
import Image from "next/image";
import { DGABreadcrumbs } from "components/Shared/dga-breadcrumbs/DGABreadcrumbs";
import { CleanRelativeLink, extractRelativePath } from "components/Shared/DomainSplit";
import IsRtl from "components/Shared/isRtl";

interface BreadcrumbItem {
  id?: string;
  fields?: {
    LinkText?: { value?: string };
    LinkUrl?: {
      value?: {
        href?: string;
        target?: string;
      };
    };
  };
}

interface FileDetailLayoutProps {
  // Breadcrumbs
  breadcrumbsItems?: BreadcrumbItem[];

  // Title
  title?: string;

  // Image
  imageSrc?: string;
  imageAlt?: string;

  // Description
  description?: string;
}

const FileDetailLayout = ({
  breadcrumbsItems = [],
  title,
  imageSrc,
  imageAlt,
  description,
}: FileDetailLayoutProps): JSX.Element => {
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  const handleImageError = () => {
    setIsImageLoading(false);
  };

  return (
    <div className="w-full flex flex-col gap-20">
      {/* Banner Section with Breadcrumbs and Title */}
      <div className="animate-top-dga relative z-10 flex flex-col gap-5">
        {/* Background Banner */}
        <div
          className={`z-0 bg-primary-25 absolute top-0 left-0 right-0 h-full w-[200%] ${
            IsRtl() ? "translate-x-[10%]" : "-translate-x-[10%]"
          }`}
        />

        {/* Breadcrumbs */}
        {breadcrumbsItems.length > 0 && (
          <div className="relative z-10 pt-10">
            <DGABreadcrumbs
              links={breadcrumbsItems.map((item: any) => ({
                id: item?.id || "",
                label: item?.fields?.LinkText?.value || "",
                link: item?.fields?.LinkUrl?.value?.href
                  ? {
                      href: CleanRelativeLink(item?.fields?.LinkUrl?.value?.href),
                      target: item?.fields?.LinkUrl?.value?.target || "_self",
                      querystring: "",
                    }
                  : null,
              }))}
            />
          </div>
        )}

        {/* Title */}
        {title && (
          <h1 className="relative z-10 text-[30px] lg:text-[35px] 2xl:text-[40px] font-bold text-primary-1000 mb-6">
            {title}
          </h1>
        )}
      </div>

      {/* Main Content - Image and Description */}
      <div className="relative z-10 w-full">
        {/* Image */}
        {imageSrc && (
          <div className="relative w-full mb-6 min-h-[429px]">
            {/* Skeleton Loader */}
            {isImageLoading && (
              <div className="inset-0 bg-gray-200 animate-pulse rounded-lg h-full w-full" />
            )}
            {/* Image */}
            <Image
              src={extractRelativePath(imageSrc)}
              alt={imageAlt || title || "File detail image"}
              layout="fill"
              objectFit="cover"
              className={`rounded-lg transition-opacity duration-300 ${
                isImageLoading ? "opacity-0" : "opacity-100"
              }`}
              unoptimized
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>
        )}

        {/* Description */}
        {description && (
          <div className="text-[16px] text-primary-1000 text-justify mb-6">
            <RichText field={{ value: description }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FileDetailLayout;

